<script lang="ts">
    import { onDestroy } from 'svelte'
    import type { Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'

    import { gettext as _, resolve } from 'hydria/services/gettext'

    import { subaccount } from 'wdom/subaccount'
    import { tickValueToText, TTL_TICKS_S } from 'wdom/zone/zone'
    import type { Zone } from 'wdom/zone/zone'

    import InputRange from 'hydria/components/InputRange.svelte'
    import HelpButton from 'wdom/components/HelpButton.svelte'

    interface Props {
        status: Writable<'loading' | 'ready' | 'not-found' | 'error'>
        zone: Writable<Zone | undefined>
        busy: Writable<boolean | string>
        zoneEdit: Writable<Zone | undefined>
        class?: string | null
    }

    let { status, zone, busy, zoneEdit, class: classes = null }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function ttlToPosition(ttl_seconds: number) {
        let i = TTL_TICKS_S.indexOf(ttl_seconds)
        if (i < 0) {
            // Well, this sucks… a zone with a TTL not bound on a specific stop
            // of our list.
            for (const [j, ttl] of TTL_TICKS_S.entries()) {
                if (ttl_seconds <= ttl) {
                    i = j
                }
            }
        }
        if (i < 0) {
            // !?
            i = 0
        }
        return i
    }

    let defaultPos: number = $state(0)
    let minimumPos: number = $state(0)

    // Update local positions when something changes externally
    unsubs.push(
        zoneEdit.subscribe((ze) => {
            if (ze) {
                if (ze.default_ttl != TTL_TICKS_S[defaultPos]) {
                    defaultPos = ttlToPosition(ze.default_ttl)
                }
                if (ze.minimum_ttl != TTL_TICKS_S[minimumPos]) {
                    minimumPos = ttlToPosition(ze.minimum_ttl)
                }
            }
        }),
    )

    // Update external data when local positions change
    $effect(() => {
        if (defaultPos !== undefined && $zoneEdit && TTL_TICKS_S[defaultPos] != $zoneEdit.default_ttl) {
            zoneEdit.update((ze) => {
                if (ze) ze.default_ttl = TTL_TICKS_S[defaultPos]
                return ze
            })
        }
    })
    $effect(() => {
        if (minimumPos !== undefined && $zoneEdit && TTL_TICKS_S[minimumPos] != $zoneEdit.minimum_ttl) {
            zoneEdit.update((ze) => {
                if (ze) ze.minimum_ttl = TTL_TICKS_S[minimumPos]
                return ze
            })
        }
    })
</script>

{#if $status == 'ready'}
    <div>
        <div class="standalone-title text-primary d-flex justify-content-between gap-4">
            <span>{$_('Zone configuration')}</span>
            <HelpButton tag={'zone-config'} />
        </div>
        <div class="card{classes ? ` ${classes}` : ''}">
            <div class="card-body">
                <form
                    onsubmit={(e) => {
                        e.preventDefault()
                    }}
                >
                    <div class="form-group-zone-config">
                        <div><label for="zoneDefaultTtl" class="col-form-label">{$_('Default TTL')}</label></div>
                        <div>
                            <div class="slider-content">
                                <InputRange min={0} max={TTL_TICKS_S.length - 1} bind:value={defaultPos} id="zoneDefaultTtl" disabled={!!$busy || !$subaccount.zone} />
                            </div>
                        </div>
                        <div>
                            <p>{$resolve(tickValueToText(TTL_TICKS_S[defaultPos]))}</p>
                        </div>
                        <div>
                            {#if $subaccount.zone}
                                <button type="button" class="btn btn-outline-secondary btn-sm" disabled={!$zone || $zone.default_ttl == TTL_TICKS_S[defaultPos] || !!$busy} onclick={() => (defaultPos = ttlToPosition($zone ? $zone.default_ttl : 86400))}><Icon inline icon="fa:undo" /></button>
                            {/if}
                        </div>
                        <div><label for="zoneMinimumTtl" class="col-form-label">{$_('Minimum TTL')}</label></div>
                        <div>
                            <div class="slider-content">
                                <InputRange min={0} max={TTL_TICKS_S.length - 1} bind:value={minimumPos} id="zoneMinimumTtl" disabled={!!$busy || !$subaccount.zone} />
                            </div>
                        </div>
                        <div>
                            <p>{$resolve(tickValueToText(TTL_TICKS_S[minimumPos]))}</p>
                        </div>
                        <div>
                            {#if $subaccount.zone}
                                <button type="button" class="btn btn-outline-secondary btn-sm" disabled={!$zone || $zone.minimum_ttl == TTL_TICKS_S[minimumPos] || !!$busy} onclick={() => (minimumPos = ttlToPosition($zone ? $zone.minimum_ttl : 86400))}><Icon inline icon="fa:undo" /></button>
                            {/if}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
{/if}
